import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DATA_RECOVERY } from '@/constants/testIds';
import { AlertTriangle, HardDrive } from 'lucide-react';
import { fetchBoard, fetchHealth } from './api';
import { columnTone } from './styles';
import TicketCard from './TicketCard';

const REFRESH_INTERVAL_MS = 30000;
const ROTATE_INTERVAL_MS = 30000;
const CARDS_PER_COLUMN = 5; // 1080p TV with 5 wider columns

const HEALTH_PILL = {
  ok:             { label: 'Live',           tone: 'bg-emerald-500/20 text-emerald-100 border-emerald-400/60', dot: 'bg-emerald-400' },
  sample:         { label: 'Sample',         tone: 'bg-amber-500/20   text-amber-100   border-amber-400/60',   dot: 'bg-amber-400'   },
  error:          { label: 'API Error',      tone: 'bg-rose-500/20    text-rose-100    border-rose-400/60',    dot: 'bg-rose-400'    },
  not_configured: { label: 'Not Configured', tone: 'bg-rose-500/20    text-rose-100    border-rose-400/60',    dot: 'bg-rose-400'    },
  loading:        { label: 'Checking…',      tone: 'bg-slate-500/20   text-slate-100   border-slate-400/60',   dot: 'bg-slate-400'   },
};

const SUMMARY_TILES = [
  { key: 'pending_diagnostic', label: 'Pending Diagnostic', tone: 'slate' },
  { key: 'being_diagnosed', label: 'Being Diagnosed', tone: 'indigo' },
  { key: 'recovery', label: 'Recovery Active', tone: 'blue' },
  { key: 'file_verification', label: 'Verification', tone: 'violet' },
  { key: 'ready_for_pickup', label: 'Ready', tone: 'emerald' },
  { key: 'blocked', label: 'Blocked', tone: 'amber' },
  { key: 'stalled', label: 'Stalled', tone: 'rose' },
];

const TONE_CLASSES = {
  slate: 'bg-slate-900/70 border-slate-700 text-slate-100',
  indigo: 'bg-indigo-900/40 border-indigo-700/60 text-indigo-50',
  cyan: 'bg-cyan-900/40 border-cyan-700/60 text-cyan-50',
  blue: 'bg-blue-900/40 border-blue-700/60 text-blue-50',
  violet: 'bg-violet-900/40 border-violet-700/60 text-violet-50',
  emerald: 'bg-emerald-900/40 border-emerald-700/60 text-emerald-50',
  amber: 'bg-amber-900/40 border-amber-700/60 text-amber-50',
  rose: 'bg-rose-900/40 border-rose-700/60 text-rose-50',
};

export default function DataRecoveryBoard() {
  const [board, setBoard] = useState(null);
  const [health, setHealth] = useState(null);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [rotateTick, setRotateTick] = useState(0);
  const refreshTimer = useRef(null);
  const rotateTimer = useRef(null);

  const load = useCallback(async () => {
    try {
      const [data, h] = await Promise.all([
        fetchBoard(),
        fetchHealth().catch(() => null),
      ]);
      setBoard(data);
      if (h) setHealth(h);
      setError(null);
      setLastUpdated(new Date());
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load board');
    }
  }, []);

  // Initial + interval load
  useEffect(() => {
    load();
    refreshTimer.current = setInterval(load, REFRESH_INTERVAL_MS);
    rotateTimer.current = setInterval(() => setRotateTick((t) => t + 1), ROTATE_INTERVAL_MS);
    return () => {
      if (refreshTimer.current) clearInterval(refreshTimer.current);
      if (rotateTimer.current) clearInterval(rotateTimer.current);
    };
  }, [load]);

  const columns = board?.columns || [];
  const source = board?.source;
  const totals = board?.totals || {};

  return (
    <div
      data-testid={DATA_RECOVERY.page}
      className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-950 to-slate-900 text-slate-100 select-none"
    >
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-950/70 backdrop-blur">
        <div className="w-full px-4 py-2 flex items-center gap-4">
          <HardDrive className="w-7 h-7 text-cyan-400" />
          <h1
            data-testid={DATA_RECOVERY.title}
            className="text-xl lg:text-2xl font-semibold tracking-tight"
          >
            ProTek Mission Board
            <span className="text-slate-500 font-normal mx-2">·</span>
            <span className="text-cyan-300">Data Recovery</span>
          </h1>
          <span
            data-testid={DATA_RECOVERY.lastUpdated}
            className="ml-auto text-xs lg:text-sm font-mono text-slate-400"
          >
            {lastUpdated
              ? `Last updated ${lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`
              : 'Loading…'}
          </span>
          <HealthPill health={health} />
        </div>
      </header>

      <main className="w-full px-4 py-3 space-y-3">
        {/* Config / data-source warning */}
        {source && source.warnings && source.warnings.length > 0 && (
          <div
            data-testid={DATA_RECOVERY.configWarning}
            className={`rounded-md border px-4 py-1.5 text-sm flex items-center gap-2 ${
              source.mode === 'sample'
                ? 'bg-amber-500/10 border-amber-500/40 text-amber-100'
                : 'bg-rose-500/10 border-rose-500/40 text-rose-100'
            }`}
          >
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span className="font-medium">
              {source.mode === 'sample' ? 'Sample data' : 'RepairDesk not configured'}:
            </span>
            <span className="text-slate-200/90">{source.warnings.join(' ')}</span>
          </div>
        )}

        {error && (
          <div className="rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-100 px-4 py-1.5 text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            <span>{error}</span>
          </div>
        )}

        {/* Summary strip — compact */}
        <section
          data-testid={DATA_RECOVERY.summaryStrip}
          className="grid grid-cols-7 gap-2"
        >
          {SUMMARY_TILES.map((t) => (
            <div
              key={t.key}
              data-testid={DATA_RECOVERY.summaryStat}
              data-stat-key={t.key}
              className={`rounded-md border px-3 py-1.5 ${TONE_CLASSES[t.tone]}`}
            >
              <div className="text-[10px] lg:text-xs uppercase tracking-wider opacity-80 leading-tight">
                {t.label}
              </div>
              <div className="text-2xl lg:text-[28px] font-mono font-semibold leading-tight">
                {totals[t.key] ?? '—'}
              </div>
            </div>
          ))}
        </section>

        {/* Board: 6 columns, full width */}
        <section
          data-testid={DATA_RECOVERY.boardGrid}
          className="grid grid-cols-6 gap-3"
        >
          {columns.map((col) => (
            <BoardColumn key={col.phase} column={col} rotateTick={rotateTick} />
          ))}
        </section>
      </main>
    </div>
  );
}

function BoardColumn({ column, rotateTick }) {
  const tone = columnTone(column.phase);
  const total = column.tickets.length;
  const pages = Math.max(1, Math.ceil(total / CARDS_PER_COLUMN));
  const pageIndex = pages > 0 ? rotateTick % pages : 0;
  const start = pageIndex * CARDS_PER_COLUMN;
  const visible = column.tickets.slice(start, start + CARDS_PER_COLUMN);
  const overflow = total - (start + visible.length);

  return (
    <div
      data-testid={DATA_RECOVERY.column}
      data-phase={column.phase}
      className="flex flex-col rounded-md border border-slate-800 bg-slate-900/30 overflow-hidden min-h-[78vh]"
    >
      <div
        data-testid={DATA_RECOVERY.columnHeader}
        className={`flex items-center justify-between px-3 py-2 ${tone.header}`}
      >
        <span className="text-base lg:text-lg font-semibold tracking-wide uppercase">
          {column.label}
        </span>
        <span
          data-testid={DATA_RECOVERY.columnCount}
          className={`font-mono text-base lg:text-lg bg-black/30 rounded-full px-3 py-0.5 ${tone.accent}`}
        >
          {column.count}
        </span>
      </div>

      <div className="p-2 space-y-2 grow overflow-hidden">
        {visible.length === 0 ? (
          <div className="text-center text-slate-600 text-sm italic py-8">—</div>
        ) : (
          visible.map((t) => <TicketCard key={t.ticket_id} ticket={t} />)
        )}
      </div>

      {pages > 1 && (
        <div
          data-testid={DATA_RECOVERY.overflowIndicator}
          className="px-3 py-1.5 border-t border-slate-800 bg-slate-950/60 flex items-center justify-between text-xs text-slate-400"
        >
          <span>
            Page {pageIndex + 1} / {pages}
          </span>
          {overflow > 0 ? <span>+{overflow} more</span> : <span>rotating</span>}
        </div>
      )}
    </div>
  );
}

function HealthPill({ health }) {
  const key = health?.status || 'loading';
  const cfg = HEALTH_PILL[key] || HEALTH_PILL.loading;
  const tooltip = health?.message
    || (key === 'ok' && health?.latency_ms != null ? `RepairDesk responded in ${health.latency_ms} ms` : '')
    || (key === 'ok' ? 'RepairDesk live' : '');
  return (
    <span
      data-testid={DATA_RECOVERY.healthPill}
      data-status={key}
      title={tooltip}
      className={`ml-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] lg:text-xs font-semibold uppercase tracking-wider ${cfg.tone}`}
    >
      <span className={`inline-block w-2 h-2 rounded-full ${cfg.dot} ${key === 'ok' ? 'animate-pulse' : ''}`} />
      {cfg.label}
    </span>
  );
}
